import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  Icon,
  SimpleGrid,
  Image,
} from '@chakra-ui/react';
import {
  FaGift,
  FaCheckCircle,
  FaClock,
  FaHandshake,
  FaCloud,
  FaSignOutAlt,
  FaArrowRight,
  // FaRobot, // Copilot temporarily hidden
  // FaCoins,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

const Pricing = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="gray.50">
      <Helmet>
        <title>Pricing & Credits | SRS — GPU Cloud CFD Simulation</title>
        <meta name="description" content="Simple pay-as-you-go pricing for GPU-accelerated Scale-Resolving Simulations. Buy credits and pay only for the GPU hours you use — no hardware, no license fees." />
        <link rel="canonical" href="https://srs.numericalai.net/pricing" />
        <meta property="og:title" content="Pricing & Credits | SRS — GPU Cloud CFD Simulation" />
        <meta property="og:description" content="Simple pay-as-you-go pricing for GPU-accelerated Scale-Resolving Simulations. Buy credits and pay only for the GPU hours you use — no hardware, no license fees." />
        <meta property="og:url" content="https://srs.numericalai.net/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pricing & Credits | SRS — GPU Cloud CFD Simulation" />
        <meta name="twitter:description" content="Simple pay-as-you-go pricing for GPU-accelerated Scale-Resolving Simulations. Buy credits and pay only for the GPU hours you use — no hardware, no license fees." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">
            <HStack
              spacing={1}
              cursor="pointer"
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }}
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: 'scale(0.98)' }}
            >
              <Image src="/simple-logo.png" alt="SRS Logo" boxSize={14} objectFit="contain" />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              {user ? (
                <Button leftIcon={<FaCloud />} colorScheme="brand" size="sm" variant="ghost" as={RouterLink} to="/dashboard">
                  Dashboard
                </Button>
              ) : (
                <Button variant="ghost" as={RouterLink} to="/signin">Sign In</Button>
              )}
              {user ? (
                <Button leftIcon={<FaSignOutAlt />} variant="outline" size="sm" onClick={() => logout()}>
                  Sign Out
                </Button>
              ) : (
                <Button colorScheme="brand" as={RouterLink} to="/create-user">Get Started</Button>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Hero */}
      <Box bg="linear-gradient(135deg, #155e75 0%, #0891b2 100%)" color="white" py={16}>
        <Container maxW="container.lg" textAlign="center">
          <VStack spacing={4}>
            <Heading as="h1" size="2xl">Simple, Usage-Based Pricing</Heading>
            <Text fontSize="lg" maxW="2xl" opacity={0.9}>
              Pay only for the compute time you need. No subscriptions, no hidden fees — just one-time credit purchases that never expire for 12 months.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Plans */}
      <Container maxW="container.xl" py={16}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {/* €1 Starter Plan */}
          <Box
            border="2px"
            borderColor="gray.200"
            rounded="xl"
            p={6}
            textAlign="center"
            bg="white"
            shadow="sm"
            _hover={{ borderColor: 'gray.300', shadow: 'md' }}
            transition="all 0.2s"
            display="flex"
            flexDirection="column"
            minH="420px"
          >
            <VStack spacing={4} flex="1">
              <VStack spacing={2}>
                <Icon as={FaGift} color="gray.500" boxSize={8} />
                <Heading size="md" color="gray.700">€1 Starter</Heading>
                
              </VStack>

              <VStack spacing={2} align="start" w="full" flex="1">
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm">Unlimited storage</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm">2 hours compute time</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm">Basic GPU access</Text>
                </HStack>
              </VStack>

              <Button
                colorScheme="gray"
                variant="outline"
                size="sm"
                w="full"
                mt="auto"
                as={RouterLink}
                to={user ? '/buy-credits' : '/create-user'}
              >
                {user ? 'Start with €1' : 'Get Started'}
              </Button>
            </VStack>
          </Box>

          {/* 10 Hours Plan */}
          <Box
            border="2px"
            borderColor="brand.500"
            rounded="xl"
            p={6}
            textAlign="center"
            bg="white"
            shadow="md"
            _hover={{ shadow: 'lg' }}
            transition="all 0.2s"
            display="flex"
            flexDirection="column"
            minH="420px"
          >
            <VStack spacing={4} flex="1">
              <VStack spacing={2}>
                <Icon as={FaClock} color="brand.500" boxSize={8} />
                <Heading size="md" color="brand.600">10 Hours</Heading>
                <Text fontSize="3xl" fontWeight="bold" color="brand.600">€150</Text>
                <Text fontSize="sm" color="gray.500">excl. VAT</Text>
                <Text fontSize="sm" color="gray.500">one-time purchase</Text>
              </VStack>

              <VStack spacing={2} align="start" w="full" flex="1">
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                  <Text fontSize="sm">Priority queue</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                  <Text fontSize="sm">Unlimited storage</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                  <Text fontSize="sm">10 hours compute time</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                  <Text fontSize="sm">High-performance GPUs</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                  <Text fontSize="sm">Email support</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                  <Text fontSize="sm">AI Integration</Text>
                </HStack>
              </VStack>

              <Button
                colorScheme="brand"
                variant="solid"
                size="sm"
                w="full"
                mt="auto"
                as={RouterLink}
                to={user ? '/buy-credits' : '/create-user'}
              >
                {user ? 'Buy 10 Hours' : 'Get Started'}
              </Button>
            </VStack>
          </Box>

          {/* 22 Hours Plan */}
          <Box
            border="2px"
            borderColor="green.300"
            rounded="xl"
            p={6}
            textAlign="center"
            bg="white"
            shadow="sm"
            _hover={{ borderColor: 'green.400', shadow: 'lg' }}
            transition="all 0.2s"
            position="relative"
            display="flex"
            flexDirection="column"
            minH="420px"
          >
            <Badge
              colorScheme="green"
              position="absolute"
              top="-10px"
              left="50%"
              transform="translateX(-50%)"
              px={3}
              py={1}
              fontSize="xs"
            >
              SAVE €20
            </Badge>
            <VStack spacing={4} flex="1">
              <VStack spacing={2}>
                <Icon as={FaClock} color="green.500" boxSize={8} />
                <Heading size="md" color="green.600">22 Hours</Heading>
                <Text fontSize="3xl" fontWeight="bold" color="green.600">€300</Text>
                <Text fontSize="sm" color="gray.500">excl. VAT</Text>
                <Text fontSize="sm" color="gray.500">one-time purchase</Text>
              </VStack>

              <VStack spacing={2} align="start" w="full" flex="1">
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm">Priority queue</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm">Unlimited storage</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm">22 hours compute time</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm">High-performance GPUs</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm">Email support</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm">AI Integration</Text>
                </HStack>
              </VStack>

              <Button
                colorScheme="green"
                variant="solid"
                size="sm"
                w="full"
                mt="auto"
                as={RouterLink}
                to={user ? '/buy-credits' : '/create-user'}
              >
                {user ? 'Buy 22 Hours' : 'Get Started'}
              </Button>
            </VStack>
          </Box>

          {/* Enterprise Plan */}
          <Box
            border="2px"
            borderColor="yellow.300"
            rounded="xl"
            p={6}
            textAlign="center"
            bg="white"
            shadow="sm"
            _hover={{ borderColor: 'yellow.400', shadow: 'lg' }}
            transition="all 0.2s"
            display="flex"
            flexDirection="column"
            minH="420px"
          >
            <VStack spacing={4} flex="1">
              <VStack spacing={2}>
                <Icon as={FaHandshake} color="yellow.500" boxSize={8} />
                <Heading size="md" color="yellow.600">Enterprise</Heading>
                <Text fontSize="3xl" fontWeight="bold" color="yellow.600">Custom</Text>
                <Text fontSize="sm" color="gray.500">custom pricing</Text>
              </VStack>

              <VStack spacing={2} align="start" w="full" flex="1" textAlign="left">
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                  <Text fontSize="sm">Unlimited storage</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                  <Text fontSize="sm">Unlimited compute time</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                  <Text fontSize="sm">Unlimited concurrent simulations</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                  <Text fontSize="sm">Premium GPU clusters</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                  <Text fontSize="sm">Dedicated resources</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                  <Text fontSize="sm">Priority support</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                  <Text fontSize="sm">Custom integrations</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                  <Text fontSize="sm">Advanced AI Integration</Text>
                </HStack>
              </VStack>

              <Button
                colorScheme="yellow"
                size="sm"
                w="full"
                mt="auto"
                as={RouterLink}
                to={user ? '/buy-credits' : '/create-user'}
              >
                Contact Sales
              </Button>
            </VStack>
          </Box>
        </SimpleGrid>

        {/* Copilot credits section temporarily hidden */}

        {/* Additional Info */}
        <VStack spacing={6} mt={16} textAlign="center">
          <Heading size="md" color="gray.700">Have a Promotion Code?</Heading>
          <Text color="gray.600" maxW="lg">
            Sign in to your account and apply your coupon code on the purchase page to get discounts on any plan.
          </Text>
          <Button
            colorScheme="brand"
            variant="outline"
            rightIcon={<FaArrowRight />}
            as={RouterLink}
            to={user ? '/buy-credits' : '/signin'}
          >
            {user ? 'Go to Purchase Page' : 'Sign In to Apply Coupon'}
          </Button>
        </VStack>
      </Container>

      <Footer />
    </Box>
  );
};

export default Pricing;
